import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  isWithdrawalDisabled,
  isPortalInactive,
} from '../services/orderLookupService';
import {
  BankAccount,
  countries,
  CreateReturn,
  CreateReturnItem,
  LocationStateSummary,
  PresignedUrlResponse,
  RadioOption,
  Return,
  ReturnFullProduct,
  ReturnProduct,
  ShippingAddress,
} from '../utils/types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  API_URL,
  formatPrice,
  getPresignedUrl,
  photoFileExtension,
  JWT_TOKEN,
  SHOP_ID,
  shopLabel,
  termsUrlOrNull,
  uploadToS3,
} from '../utils/helpers';
import {
  computeReturnProductTotal,
  computeReturnTotalPrice,
} from '../utils/returnPricing';
import { evaluateReturnMethodRules } from '../utils/returnMethodRules';
import {
  DetailsError,
  detailsErrorOf,
  detailsSettingsOf,
  PortalDetailsSettings,
  visibleDetails,
} from '../utils/portalDetails';
import {
  clearMethodsDrafts,
  readMethodsDraft,
  saveMethodsDraft,
} from '../utils/methodsDraft';
import AddressModal from './AddressModal';
import IbanModal from './IbanModal';
import { useTranslation } from 'react-i18next';
import { IbanForm } from './IbanForm';
import Stepper from './Stepper';
import ActionBar from './ActionBar';
import { useStandaloneQr } from '../contexts/StandaloneQrContext';
import { standaloneNavigationState } from '../utils/qrEntry';
import {
  credentialsForOrder,
  readPortalSession,
} from '../services/portalCredentials';
import {
  standaloneCredentials,
  standaloneOrderId,
} from '../services/standaloneQrSession';

const seedPickupCountry = (address: ShippingAddress) => {
  if (address.country_code?.trim()) {
    return { country: address.country, country_code: address.country_code };
  }
  const country = countries.find(
    country =>
      country.name.toLowerCase() === address.country?.trim().toLowerCase()
  );
  return { country: country?.name ?? '', country_code: country?.code ?? null };
};

const ReturnAndPaymentMethodPage: React.FC = () => {
  const { t } = useTranslation(['translation', 'dynamic']);
  const location = useLocation();
  const state = location.state as LocationStateSummary;
  // #95 (E11): the reason's own text when no shop label could be loaded
  const reasonLabel = (reason: string): string =>
    (state.return_reasons ?? []).find(r => String(r.value) === String(reason))
      ?.label ?? '';
  const order_token = state.order_token;
  const order = order_token.order_details;
  // #149: a case C return of a withdrawal declaration (no reason sent: the
  // shop's system reason applies)
  const withdrawalId = state.withdrawal_id;
  const withdrawalMode = withdrawalId !== undefined;
  const returnProducts = state.return_products;
  // #496: the shop's "Detalii" setting the products step applied (absent:
  // SHOWN / 500, as before)
  const detailsSettings = detailsSettingsOf(state);
  // the field visibility of PHOTO_ONLY; an older history entry has no
  // requirePhoto: a line with photos had one (as Back assumes)
  const linePhotoRequired = (prod: ReturnFullProduct) =>
    prod.requirePhoto ?? prod.photos.length > 0;
  // #64: a shop may have no return or refund methods; never assume a first one.
  const allReturnOptions: RadioOption[] = useMemo(
    () => state.return_methods ?? [],
    [state.return_methods]
  );
  // #128: the SET_RETURN_METHODS rules on the return reason (fe_actions) limit
  // the methods as the server limits them at submit; none left -> the
  // no-methods notice below
  const { returnOptions, matchedRuleIds } = useMemo(() => {
    const { allowedIds: allowed, matchedRuleIds } = evaluateReturnMethodRules(
      state.requirements?.fe_actions ?? [],
      returnProducts,
      state.return_reasons ?? [],
      order,
      // #149: no reason on a withdrawal return: a rule on it is unknown
      { reasonUnknown: withdrawalMode }
    );
    return {
      returnOptions:
        allowed === null
          ? allReturnOptions
          : allReturnOptions.filter(option => allowed.has(String(option.id))),
      matchedRuleIds,
    };
  }, [
    allReturnOptions,
    state.requirements,
    returnProducts,
    state.return_reasons,
    order,
    withdrawalMode,
  ]);
  const paymentOptions: RadioOption[] = state.payment_methods ?? [];
  const hasMethods = returnOptions.length > 0 && paymentOptions.length > 0;
  // Discount allocation lives in utils/returnPricing — see helper file for the
  // line-vs-unit semantic of the two `total_discount` fields. Mirrors the
  // backend computation in ReturnService.java.
  const totalPrice = computeReturnTotalPrice(returnProducts);
  const uniqueIds = new Set(returnProducts.map(product => product.id));
  const totalReturnItem = uniqueIds.size;
  // #496 (review r1 #2): the choices made before a correction of the
  // products step, restored while they are still offered
  // (review r2 #1) read from memory by the id in history; none after a
  // reload, in a new tab or for another shop / order: a fresh start
  const draft = readMethodsDraft(state.methods_draft_id, {
    shopId: order.shop_id,
    orderId: order.id,
  });
  const initialReturn =
    returnOptions.find(option => option.id === draft?.return_method_id) ??
    returnOptions[0];
  const initialPayment =
    paymentOptions.find(option => option.id === draft?.payment_method_id) ??
    paymentOptions[0];
  const initialReturnPrice = initialReturn?.cost ? initialReturn.cost : 0;
  const [showAll, setShowAll] = useState(false);
  const displayedProducts = showAll
    ? returnProducts
    : returnProducts.slice(0, 1);

  const navigate = useNavigate();
  // #72: keep the provenance of a standalone QR session in history entries
  const { active: standaloneQr, handoff, account } = useStandaloneQr();

  const [selectedReturn, setSelectedReturn] = useState<RadioOption | undefined>(
    initialReturn
  );
  const [selectedPayment, setSelectedPayment] = useState<
    RadioOption | undefined
  >(initialPayment);
  const [isModalAddressOpen, setIsModalAddressOpen] = useState(false);
  const [isModalIbanOpen, setIsModalIbanOpen] = useState(false);
  const [displayAddress, setDisplayAddress] = useState<boolean>(
    selectedReturn?.more_details ?? false
  );
  const [displayIban, setDisplayIban] = useState<boolean>(
    selectedPayment?.more_details ?? false
  );
  const [totalPayment, setTotalPayment] = useState<number>(
    totalPrice - initialReturnPrice
  );
  const normalizeAddressValue = (value: string | null | undefined) =>
    (value ?? '').normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
  const billing = order.billing_address;
  const billingZip = normalizeAddressValue(billing?.zip);
  const shippingZip = normalizeAddressValue(order.shipping_address.zip);
  const useBilling =
    order.delivered_to_pickup_point === true &&
    !!billing?.address1?.trim() &&
    !!billing?.city?.trim() &&
    (normalizeAddressValue(billing.address1) !==
      normalizeAddressValue(order.shipping_address.address1) ||
      (!!billingZip && !!shippingZip && billingZip !== shippingZip));
  const seedSource: CreateReturn['pickup_address_source'] =
    order.delivered_to_pickup_point !== true
      ? 'SHIPPING'
      : useBilling
        ? 'BILLING'
        : 'CUSTOMER';
  const seedAddress: ShippingAddress =
    seedSource === 'SHIPPING'
      ? {
          ...order.shipping_address,
          first_name: order.customer.first_name,
          last_name: order.customer.last_name,
          phone: order.customer.phone,
        }
      : useBilling && billing
        ? {
            ...billing,
            ...seedPickupCountry(billing),
            first_name: billing.first_name?.trim() || order.customer.first_name,
            last_name: billing.last_name?.trim() || order.customer.last_name,
            phone: billing.phone?.trim() || order.customer.phone,
          }
        : {
            id: null,
            first_name: order.customer.first_name,
            last_name: order.customer.last_name,
            phone: order.customer.phone,
            address1: '',
            address2: null,
            city: '',
            zip: '',
            province: '',
            province_code: null,
            ...seedPickupCountry(order.shipping_address),
          };
  const [returnAddress, setReturnAddress] = useState<ShippingAddress>(
    draft?.return_address ?? seedAddress
  );
  const addressSource = (Object.keys(seedAddress) as (keyof ShippingAddress)[])
    .filter(key => key !== 'id')
    .every(
      key =>
        normalizeAddressValue(String(returnAddress[key] ?? '')) ===
        normalizeAddressValue(String(seedAddress[key] ?? ''))
    )
    ? seedSource
    : 'CUSTOMER';
  const needsPickupAddress =
    displayAddress &&
    order.delivered_to_pickup_point === true &&
    ![
      returnAddress.address1,
      returnAddress.city,
      returnAddress.zip,
      returnAddress.phone,
      returnAddress.country,
      returnAddress.country_code,
    ].every(value => value?.trim());
  const [bankAccount, setBankAccount] = useState<BankAccount | undefined>(
    draft?.bank_account
  );
  const [withdrawalStop, setWithdrawalStop] = useState<
    'disabledByShop' | 'portalInactive' | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  // #51: a shop with a return conditions page requires the agreement
  const termsUrl = termsUrlOrNull(state.terms_and_conditions_url);
  const [termsAccepted, setTermsAccepted] = useState(
    draft?.terms_accepted === true
  );

  // Content memo survives rerenders and StrictMode effect replay, but is local
  // to this page instance. Sort sets so catalog ordering is not a new offer.
  const sentOffers = useRef(new Set<string>());
  const sortedIds = (ids: number[]) =>
    Array.from(new Set(ids)).sort((a, b) => a - b);
  const offerBody = state.offer_correlation_id
    ? JSON.stringify({
        correlation_id: state.offer_correlation_id,
        step: 'METHODS',
        // The no-methods view renders neither radio group.
        return_method_ids: sortedIds(
          hasMethods ? returnOptions.map(option => option.id) : []
        ),
        refund_method_ids: sortedIds(
          hasMethods ? paymentOptions.map(option => option.id) : []
        ),
        matched_frontend_rule_ids: sortedIds(matchedRuleIds),
      })
    : null;

  // Read current credentials on each render so a skipped offer can be sent
  // when its session becomes available. Never authenticate from history state.
  const offerAuth = standaloneQr
    ? standaloneCredentials()
    : withdrawalMode
      ? credentialsForOrder(false, order.shop_id, order.id)
      : { token: JWT_TOKEN, shopId: SHOP_ID };
  // Token refresh preserves the session's shop and order. Read that identity
  // from the current credential source, never from the replayable route alone.
  const currentOfferSession = standaloneQr
    ? { shopId: offerAuth?.shopId, orderId: standaloneOrderId() }
    : readPortalSession();
  const currentOfferShopId = currentOfferSession?.shopId;
  const currentOfferOrderId = currentOfferSession?.orderId;
  const offerToken = offerAuth?.token;
  const offerShopId = offerAuth?.shopId;
  const offerSession = state.offer_session;

  useEffect(() => {
    if (error || offerBody === null || sentOffers.current.has(offerBody))
      return;
    if (
      !offerToken ||
      !offerSession ||
      offerSession.shopId !== currentOfferShopId ||
      offerSession.orderId !== currentOfferOrderId ||
      offerSession.shopId !== offerShopId ||
      offerSession.shopId !== order.shop_id ||
      offerSession.orderId !== order.id
    )
      return;
    sentOffers.current.add(offerBody);
    try {
      void fetch(API_URL + '/rules/offer-shown', {
        method: 'POST',
        keepalive: true,
        credentials: 'omit',
        headers: {
          Authorization: 'Bearer ' + offerToken,
          'Shop-Id': String(offerShopId),
          'Content-Type': 'application/json',
        },
        body: offerBody,
      }).catch(() => undefined);
    } catch {
      // Diagnostic only: no retry, no form state changes, even on sync errors.
    }
  }, [
    offerBody,
    error,
    offerToken,
    offerShopId,
    offerSession,
    currentOfferShopId,
    currentOfferOrderId,
    order.shop_id,
    order.id,
  ]);

  const returnOptionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (returnOptionsRef.current) {
        returnOptionsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleCloseAddressModal = () => {
    setIsModalAddressOpen(false);
  };

  const handleSubmitAddressForm = (data: ShippingAddress) => {
    setIsModalAddressOpen(false);
    setReturnAddress(data);
  };

  const handleOpenAddressModal = () => {
    setIsModalAddressOpen(true);
  };

  const handleCloseIbanModal = () => {
    setIsModalIbanOpen(false);
  };

  const handleSubmitIbanForm = (data: BankAccount) => {
    setIsModalIbanOpen(false);
    setBankAccount(data);
  };

  const handleOpenIbanModal = () => {
    setIsModalIbanOpen(true);
  };

  const handleReturnChange = (value: number) => {
    const optionSelected = returnOptions.find(x => x.id === value);
    if (!optionSelected) return;
    setSelectedReturn(optionSelected);
    setDisplayAddress(optionSelected.more_details);
    if (optionSelected.cost && optionSelected.cost > 0) {
      setTotalPayment(totalPrice - optionSelected.cost);
    } else {
      setTotalPayment(totalPrice);
    }
  };

  const handlePaymentChange = (value: number) => {
    const optionSelected = paymentOptions.find(x => x.id === value);
    if (!optionSelected) return;
    setSelectedPayment(optionSelected);
    setDisplayIban(optionSelected.more_details);
  };

  const handleSubmit = async () => {
    if (!selectedReturn || !selectedPayment) return;
    if (needsPickupAddress) {
      setIsModalAddressOpen(true);
      return;
    }
    // #66: the API takes a bare extension, never the MIME type. Validate every
    // photo before any upload so a bad one leaves no orphan S3 objects.
    const photoExts = new Map<File, string>();
    for (const prod of returnProducts) {
      for (const photo of prod.photos) {
        const ext = photoFileExtension(photo);
        if (!ext) {
          setError(t('errors.unsupportedPhotoType'));
          return;
        }
        photoExts.set(photo, ext);
      }
    }
    // #154 (audit #1, Codex r1 #2): a case C return uses the one credential
    // resolver of the withdrawal flow (as its loader did) for the uploads and
    // the submission, and never a session of another shop or another order
    // (checked before the uploads and again before the submission); the
    // ordinary return keeps the in-memory credentials
    const resolveAuth = (): { token: string; shopId: number } | null =>
      withdrawalMode
        ? credentialsForOrder(standaloneQr, order.shop_id, order.id)
        : { token: JWT_TOKEN, shopId: SHOP_ID };
    const auth = resolveAuth();
    if (auth === null) {
      setError(t('withdrawal.sessionExpired'));
      return;
    }
    try {
      const createReturnProducts: CreateReturnItem[] = [];
      for (const prod of returnProducts) {
        const keys: string[] = [];
        for (const photo of prod.photos) {
          const presigned: PresignedUrlResponse = await getPresignedUrl(
            photoExts.get(photo) as string,
            auth.token,
            auth.shopId.toString()
          );
          await uploadToS3(photo, presigned.url);
          keys.push(presigned.key);
        }
        createReturnProducts.push({
          order_item_id: prod.id,
          // #496: a text the products step does not show is never sent
          description:
            prod.serverRequiresDetails === true
              ? prod.details
              : visibleDetails(
                  detailsSettings.mode,
                  linePhotoRequired(prod),
                  prod.details
                ),
          quantity: prod.quantityReturn,
          photo_keys: keys,
          // #149: the key is omitted (never 0) on a withdrawal return
          ...(withdrawalMode ? {} : { return_reason_id: Number(prod.reason) }),
          exchange_product: prod.exchangeProduct,
        });
      }
      const createReturnData: CreateReturn = {
        order_id: order.id,
        order_name: order.order_name,
        client_email: order.customer.email,
        pickup_address: JSON.stringify(returnAddress),
        ...(displayAddress ? { pickup_address_source: addressSource } : {}),
        iban: bankAccount ? bankAccount.iban : null,
        bank_name:
          selectedPayment.ask_bank_name === false
            ? null
            : bankAccount?.bank || null,
        bank_customer_name: bankAccount ? bankAccount.customer : null,
        payment_method_id: selectedPayment.id,
        return_method_id: selectedReturn.id,
        // #80: the pickup-point map is disabled; no point is chosen here
        return_method_point_id: null,
        return_method_point_merchant_connector_id: null,
        return_method_point_name: null,
        return_products: createReturnProducts,
        ...(withdrawalMode ? { withdrawal_id: withdrawalId } : {}),
      };
      const submitAuth = resolveAuth();
      if (submitAuth === null) {
        setError(t('withdrawal.sessionExpired'));
        return;
      }
      const response = await fetch(`${API_URL}/returns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + submitAuth.token,
          'Shop-Id': submitAuth.shopId.toString(),
        },
        body: JSON.stringify(createReturnData),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        if (
          withdrawalMode &&
          (isWithdrawalDisabled(response.status, body) ||
            isPortalInactive(response.status, body))
        ) {
          setWithdrawalStop(
            isWithdrawalDisabled(response.status, body)
              ? 'disabledByShop'
              : 'portalInactive'
          );
          return;
        }
        // #496: the text of a line was refused (a setting changed during
        // the session): back to the products step with the current setting
        // and the error under that line
        const detailsError = detailsErrorOf(response.status, body);
        if (detailsError) {
          const reloaded = await reloadDetailsSettings(submitAuth);
          goBackToProducts(
            {
              // review r1 #1: DESCRIPTION_REQUIRED proves the server is in
              // SHOWN for that line, whatever was cached or reloaded
              mode:
                detailsError.code === 'DESCRIPTION_REQUIRED'
                  ? 'SHOWN'
                  : (reloaded?.mode ?? detailsSettings.mode),
              maxLength:
                reloaded?.maxLength ??
                detailsError.max_length ??
                detailsSettings.maxLength,
            },
            detailsError
          );
          return;
        }
        setError(
          body?.code === 'SECOND_RETURN_NOT_ALLOWED'
            ? t('forms.secondReturnNotAllowed')
            : response.status === 400 &&
                body?.code === 'PICKUP_ADDRESS_IS_PICKUP_POINT'
              ? t('errors.pickupAddressIsPickupPoint')
              : typeof body?.message === 'string' && body.message.trim()
                ? body.message
                : t('errors.createReturn')
        );
        return;
      }

      const createdReturn: Return = await response.json();
      // #496 (review r2 #1): the return exists; no choice survives it
      clearMethodsDrafts();
      // #149: the status page links back to the statement with the complete
      // continuation context
      const createdState = {
        justCreated: true,
        ...(withdrawalMode && state.withdrawal_receipt
          ? {
              withdrawal: {
                id: withdrawalId,
                receipt: state.withdrawal_receipt,
                order,
                proof: state.proof,
                shop: state.shop,
                standalone: standaloneQr,
              },
            }
          : {}),
      };
      // #105 P4-A: GET /returns/{id} now requires a signed sig. The create response carries the
      // backend-computed tracking_sig; pass it through so the tracking page can read the return.
      // The return WAS created, so always navigate away from the form (re-submitting would place a
      // duplicate return). If the response somehow carried no tracking_sig (deploy skew), redirect
      // to a dedicated "created but tracking link unavailable" state instead of an unsigned URL
      // that would 403. `replace` so the back button can't return to the (consumed) form.
      if (!createdReturn.tracking_sig) {
        navigate(
          '/return/' + createdReturn.id + '?tracking_link_unavailable=1',
          // #101: same one-shot "created" flag as the signed path below
          { replace: true, state: createdState }
        );
        return;
      }
      navigate(
        '/return/' +
          createdReturn.id +
          '?sig=' +
          encodeURIComponent(createdReturn.tracking_sig),
        // #42 / #101: ReturnPage shows the success heading once for this flag and then consumes it
        // (not on a reload or on later email / QR permalink visits).
        { replace: true, state: createdState }
      );
    } catch {
      setError(t('errors.createReturn'));
    }
  };

  // #496: GET /settings again after a refused text; null when it fails
  // (the caller keeps what it has)
  const reloadDetailsSettings = async (auth: {
    token: string;
    shopId: number;
  }): Promise<PortalDetailsSettings | null> => {
    try {
      const response = await fetch(`${API_URL}/settings`, {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + auth.token,
          'Shop-Id': auth.shopId.toString(),
        },
      });
      if (!response.ok) return null;
      return detailsSettingsOf(await response.json());
    } catch {
      return null;
    }
  };

  const handleBack = () => goBackToProducts(detailsSettings);

  const goBackToProducts = (
    details: PortalDetailsSettings,
    detailsError?: DetailsError
  ) => {
    // one flow per tab: the previous draft is replaced
    clearMethodsDrafts();
    const draftId = saveMethodsDraft(
      {
        return_method_id: selectedReturn?.id,
        payment_method_id: selectedPayment?.id,
        return_address: returnAddress,
        bank_account: bankAccount,
        terms_accepted: termsAccepted,
      },
      { shopId: order.shop_id, orderId: order.id }
    );
    const productsToReturn: ReturnProduct[] = [];
    returnProducts.forEach(function (item) {
      productsToReturn.push({
        id: item.id,
        reason: item.reason,
        quantity: item.quantityReturn,
        details: item.details,
        photos: [...item.photos],
        requirePhoto: linePhotoRequired(item),
        // #496 (review r3): the server-required marker goes back with it
        ...(item.serverRequiresDetails ? { serverRequiresDetails: true } : {}),
        showIndividual: item.showIndividual,
        exchangeProduct: item.exchangeProduct,
      });
    });
    navigate('/order-details', {
      state: {
        order_token: order_token,
        products_to_return: productsToReturn,
        requirements: state.requirements,
        offer_correlation_id: state.offer_correlation_id,
        offer_session: state.offer_session,
        return_reasons: state.return_reasons,
        // #128: the full list - step 1 may change the reasons
        return_methods: allReturnOptions,
        payment_methods: paymentOptions,
        proof: state.proof,
        shop: state.shop,
        allow_exchanges: state.allow_exchanges,
        allow_exchange_more_expensive: state.allow_exchange_more_expensive,
        terms_and_conditions_url: state.terms_and_conditions_url,
        portal_details_mode: details.mode,
        portal_details_max_length: details.maxLength,
        ...(detailsError ? { details_error: detailsError } : {}),
        // review r1 #2 / r2 #1: the choices of this step come back on
        // Continue; they stay in memory, history gets only the draft id
        methods_draft_id: draftId,
        // #149: the withdrawal goes back with the products
        ...(withdrawalMode
          ? {
              withdrawal_id: withdrawalId,
              withdrawal_receipt: state.withdrawal_receipt,
            }
          : {}),
        ...(standaloneQr ? standaloneNavigationState(handoff, account) : {}),
      },
    });
  };

  // #108 (S3, D4): title row + order chip, as the product screen
  const orderName = order.order_name ? order.order_name : order.id;
  const chipText = `${t('orderDetails.order')} ${orderName}`;
  const titleRow = (
    <div className="xc-title-row">
      <h2 className="xc-title">{t('returnOverview.overview')}</h2>
      <span className="xc-chip" title={chipText}>
        {chipText}
      </span>
    </div>
  );

  if (withdrawalStop) {
    return (
      <div className="xc-screen">
        <div className="xc-notice xc-notice--withdraw" role="alert">
          {withdrawalStop === 'disabledByShop'
            ? t('withdrawal.disabledByShop')
            : t('withdrawal.portalInactive')}
        </div>
        <Link
          to={'/withdrawal/' + withdrawalId}
          state={{
            receipt: state.withdrawal_receipt,
            order,
            proof: state.proof,
            shop: state.shop,
            ...(standaloneQr
              ? standaloneNavigationState(handoff, account)
              : {}),
          }}
        >
          {t('withdrawal.receiptLink')}
        </Link>
      </div>
    );
  }

  if (error) {
    // #108 (S3): the form state stays in React state; "Try again" returns to
    // the filled form
    return (
      <div className="xc-screen xc-screen--wide">
        <div className="xc-notice xc-notice--error" role="alert">
          <p className="xc-notice__text">{error}</p>
          <button
            type="button"
            className="xc-btn xc-btn--sm"
            onClick={() => setError(null)}
          >
            {t('buttons.again')}
          </button>
        </div>
      </div>
    );
  }

  if (!hasMethods) {
    return (
      <>
        <div className="xc-screen xc-screen--wide">
          <Stepper currentStep={1} />
          {titleRow}
          {returnOptions.length === 0 && (
            <div className="xc-notice xc-notice--warn" role="alert">
              {t('returnOverview.noReturnMethods')}
            </div>
          )}
          {paymentOptions.length === 0 && (
            <div className="xc-notice xc-notice--warn" role="alert">
              {t('returnOverview.noPaymentMethods')}
            </div>
          )}
        </div>
        {/* #105 (S1, D7): Back only, no methods to submit; a sibling of the
          screen root so the top-level shell can push it to the bottom */}
        <ActionBar onBack={handleBack} backLabel={t('buttons.back')} />
      </>
    );
  }

  // #108 (D10): the merchant instruction lines, each only when set
  const preferencesInstruction = shopLabel(
    'dynamic:app_message.return_preferences_instruction',
    ''
  );
  const amountInstruction = shopLabel(
    'dynamic:app_message.return_amount_instruction',
    ''
  );

  const fullAddress = [
    returnAddress.address1,
    returnAddress.city,
    returnAddress.province,
    returnAddress.country,
  ].join(', ');

  // #108 (D8, r1-3): the whole row is the label of the native radio; the
  // accessible name is the title, the description and the fee describe it
  const renderOption = (
    kind: 'return' | 'payment',
    option: RadioOption,
    checked: boolean,
    onSelect: (value: number) => void,
    fee: React.ReactNode
  ) => {
    const id = `${kind}-option-${option.id}`;
    const ns = kind === 'return' ? 'return_method_' : 'payment_method_';
    const description = option.value
      ? shopLabel(
          'dynamic:' + ns + option.id + '.short_instruction',
          option.value
        )
      : '';
    return (
      <label className="xc-opt" htmlFor={id}>
        <input
          className="xc-opt__radio"
          type="radio"
          name={kind === 'return' ? 'return-method' : 'refund-method'}
          id={id}
          value={option.id}
          checked={checked}
          onChange={e => onSelect(Number(e.target.value))}
          aria-labelledby={`${id}-title`}
          aria-describedby={
            [description ? `${id}-desc` : '', fee ? `${id}-fee` : '']
              .filter(Boolean)
              .join(' ') || undefined
          }
        />
        <span className="xc-opt__label">
          <span className="xc-opt__title" id={`${id}-title`}>
            {shopLabel('dynamic:' + ns + option.id + '.name', option.label)}
          </span>
          {description && (
            <span className="xc-opt__desc" id={`${id}-desc`}>
              {description}
            </span>
          )}
        </span>
        {fee && (
          <span
            className={
              option.cost > 0 ? 'xc-opt__fee xc-opt__fee--paid' : 'xc-opt__fee'
            }
            id={`${id}-fee`}
          >
            {fee}
          </span>
        )}
      </label>
    );
  };

  return (
    <>
      <div className="xc-screen xc-screen--wide">
        <Stepper currentStep={1} />
        {titleRow}
        {preferencesInstruction && (
          <p className="xc-intro">{preferencesInstruction}</p>
        )}

        {/* Returning (mockup screen 3, quiet card) */}
        <div className="xc-card xc-card--quiet">
          <h3 className="xc-card__title xc-card__title--split">
            <span>{t('preferences.returning')}</span>
            <span className="xc-card__count">
              {t('returnWithCount', { count: totalReturnItem })}
            </span>
          </h3>
          {displayedProducts.map((item, index) => {
            const exchangeProduct = item.exchangeProduct;
            return (
              <div
                className="xc-prod"
                key={`${item.id}-${item.reason}-${index}`}
              >
                {item.image_src ? (
                  <div className="xc-prod__img">
                    <img src={item.image_src} alt={item.product_title} />
                  </div>
                ) : (
                  <div className="xc-prod__img xc-prod__img--empty">
                    <span className="xc-visually-hidden">
                      {t('products.noImage')}
                    </span>
                  </div>
                )}
                <div>
                  <p className="xc-prod__name">
                    {item.quantityReturn} {'\u00d7'} {item.product_title}
                  </p>
                  <p className="xc-prod__meta">
                    {item.variant_title}
                    {item.variant_title ? ` ${'\u00b7'} ` : ''}
                    <span className="xc-prod__price">
                      {formatPrice(
                        computeReturnProductTotal(item),
                        item.currency
                      )}
                    </span>
                  </p>
                  <div className="xc-prod__chips">
                    {/* #149: no reason on a withdrawal return */}
                    {!withdrawalMode && (
                      <span className="xc-chip xc-chip--wrap">
                        <span className="xc-visually-hidden">
                          {t('return.reason')}:{' '}
                        </span>
                        {shopLabel(
                          'dynamic:return_reason_' +
                            item.reason +
                            '.display_name',
                          reasonLabel(item.reason)
                        )}
                      </span>
                    )}
                    {exchangeProduct && (
                      <span className="xc-chip xc-chip--accent xc-chip--wrap">
                        {t('productDetails.exchangeWith')}:{' '}
                        {item.quantityReturn} {'\u00d7'}{' '}
                        {exchangeProduct.product_title}
                        {exchangeProduct.variant_title !== 'Default Title'
                          ? ` - ${exchangeProduct.variant_title}`
                          : ''}{' '}
                        {'\u00b7'}{' '}
                        {formatPrice(
                          exchangeProduct.price,
                          exchangeProduct.currency
                        )}{' '}
                        {t('return.each')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {returnProducts.length > 1 && (
            <button
              type="button"
              className="xc-btn xc-btn--link"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? t('buttons.less') : t('buttons.more')}
            </button>
          )}
        </div>

        <div className="xc-two-col">
          {/* Return methods */}
          <div className="xc-card" ref={returnOptionsRef}>
            <h3 className="xc-card__title" id="methods-title">
              {t('returnOverview.options')}
            </h3>
            <div
              className="xc-opts"
              role="radiogroup"
              aria-labelledby="methods-title"
            >
              {returnOptions.map(option => {
                const selected = selectedReturn?.id === option.id;
                return (
                  <React.Fragment key={'return' + option.id}>
                    {renderOption(
                      'return',
                      option,
                      selected,
                      handleReturnChange,
                      formatPrice(option.cost, order.currency)
                    )}
                    {/* r1-2: under the SELECTED option only; outside the
                        label (D8) */}
                    {selected && displayAddress && option.more_details && (
                      <div className="xc-opt__sub">
                        {order.delivered_to_pickup_point === true && (
                          <p className="xc-notice xc-notice--warn">
                            {addressSource === 'BILLING'
                              ? t('returnOverview.pickupAddressFromBilling')
                              : t('returnOverview.pickupAddressRequired')}
                          </p>
                        )}
                        <dl className="xc-kv">
                          <dt>{t('returnOverview.from')}</dt>
                          <dd>
                            {fullAddress} {'\u00b7'}{' '}
                            <button
                              type="button"
                              className="xc-btn xc-btn--link"
                              onClick={handleOpenAddressModal}
                            >
                              {t('buttons.edit')}
                            </button>
                          </dd>
                        </dl>
                        <AddressModal
                          required={order.delivered_to_pickup_point === true}
                          isOpen={isModalAddressOpen}
                          onClose={handleCloseAddressModal}
                          onSubmit={handleSubmitAddressForm}
                          address={returnAddress}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Refund methods (D8: no fee or bonus) */}
          <div className="xc-card">
            <h3 className="xc-card__title" id="refunds-title">
              {t('returnOverview.money')}
            </h3>
            <div
              className="xc-opts"
              role="radiogroup"
              aria-labelledby="refunds-title"
            >
              {paymentOptions.map(option => {
                const selected = selectedPayment?.id === option.id;
                return (
                  <React.Fragment key={'payment' + option.id}>
                    {renderOption(
                      'payment',
                      option,
                      selected,
                      handlePaymentChange,
                      null
                    )}
                    {selected &&
                      displayIban &&
                      option.more_details &&
                      !bankAccount && (
                        <div className="xc-opt__sub">
                          <IbanForm
                            askBankName={option.ask_bank_name}
                            onSubmit={handleSubmitIbanForm}
                            showTitle={false}
                          />
                        </div>
                      )}
                    {selected &&
                      displayIban &&
                      option.more_details &&
                      bankAccount && (
                        <div className="xc-opt__sub">
                          <dl className="xc-kv">
                            {option.ask_bank_name !== false && (
                              <>
                                <dt>{t('ibanForm.bankName')}</dt>
                                <dd>{bankAccount.bank}</dd>
                              </>
                            )}
                            <dt>{t('ibanForm.accountName')}</dt>
                            <dd>{bankAccount.customer}</dd>
                            <dt>{t('ibanForm.iban')}</dt>
                            <dd>{bankAccount.iban}</dd>
                          </dl>
                          <button
                            type="button"
                            className="xc-btn xc-btn--link"
                            onClick={handleOpenIbanModal}
                          >
                            {t('buttons.edit')}
                          </button>
                          <IbanModal
                            askBankName={option.ask_bank_name}
                            isOpen={isModalIbanOpen}
                            onClose={handleCloseIbanModal}
                            onSubmit={handleSubmitIbanForm}
                            details={bankAccount}
                          />
                        </div>
                      )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Totals and the #51 agreement */}
        <div className="xc-card">
          <div className="xc-total">
            {selectedReturn && selectedReturn.cost >= 0 && (
              <div className="xc-total__row">
                <span>{t('return.delivery')}</span>
                <span>
                  {selectedReturn.cost > 0 ? '- ' : ''}
                  {formatPrice(selectedReturn.cost, order.currency)}
                </span>
              </div>
            )}
            <div className="xc-total__row xc-total__row--big">
              <span>{t('return.total')}</span>
              <span>{formatPrice(totalPayment, order.currency)}</span>
            </div>
            {amountInstruction && (
              <p className="xc-total__note">{amountInstruction}</p>
            )}
          </div>
          {termsUrl && (
            <>
              <div className="xc-divider" />
              <label className="xc-check" htmlFor="return-conditions-agreement">
                <input
                  className="xc-check__box"
                  type="checkbox"
                  id="return-conditions-agreement"
                  required
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                />
                <span className="xc-check__label">
                  {t('return.conditionsAgreement')}{' '}
                  <a href={termsUrl} target="_blank" rel="noopener noreferrer">
                    {t('return.conditionsLinkText')}
                  </a>
                </span>
              </label>
            </>
          )}
        </div>
      </div>
      {/* #105 (S1, D7) action bar, same gates as before; a sibling of the
          screen root (the top-level shell pushes it down) */}
      <ActionBar
        onBack={handleBack}
        backLabel={t('buttons.back')}
        primaryLabel={t('buttons.createReturn')}
        onPrimary={handleSubmit}
        primaryDisabled={
          selectedReturn === undefined ||
          selectedPayment === undefined ||
          (displayIban && !bankAccount) ||
          (termsUrl !== null && !termsAccepted)
        }
      />
    </>
  );
};

export default ReturnAndPaymentMethodPage;
